import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $patchStyleText } from "@lexical/selection";
import FontDownload from "@mui/icons-material/FontDownload";
import { IconButton, Tooltip } from "@mui/material";
import { $getSelection, $isRangeSelection } from "lexical";
import { useCallback, useRef } from "react";
import { usePopover } from "../../../../../utils/hooks/use-popover";
import { ColourPicker } from "../../../../generic/components/colour-picker/colour-picker";
import { floatingToolbarStyles } from "./floating-toolbar-styles";

import type { ToolbarProps } from "./props";
import type { FC } from "react";

export const FontBackgroundColourSelect: FC<ToolbarProps & { colour: string | null }> = ({
  disabled,
  colour,
  mode,
}) => {
  const [editor] = useLexicalComposerContext();
  const [anchorRef, popover] = usePopover<HTMLButtonElement>();
  const popoverInputRef = useRef<HTMLInputElement>(null);

  const onChange = useCallback(
    (newColour: string, skipAddingToHistoryStack?: boolean) => {
      editor.update(
        () => {
          const selection = $getSelection();
          if ($isRangeSelection(selection))
            $patchStyleText(selection, { "background-color": newColour });
        },
        skipAddingToHistoryStack ? { tag: "historic" } : {},
      );
    },
    [editor],
  );

  return (
    <>
      <Tooltip title="Highlight Color">
        <span>
          <IconButton
            data-analytics-id="lexical-toolbar-highlight"
            disabled={disabled}
            data-testid="toolbar-button-highlight-color"
            aria-label="Highlight Color"
            tabIndex={-1}
            sx={{
              ...floatingToolbarStyles[mode].button,
              backgroundColor: colour || "background.neutral",
            }}
            onClick={popover.handleToggle}
            ref={anchorRef}
          >
            <FontDownload sx={floatingToolbarStyles[mode].formatIcon} />
          </IconButton>
        </span>
      </Tooltip>
      <ColourPicker
        anchorRef={anchorRef}
        popover={popover}
        colour={colour ?? "#ffffff"}
        onChange={onChange}
        inputRef={popoverInputRef}
      />
    </>
  );
};
